import { useEffect } from "react";
import { getApi } from "../api";
import { useFormStore } from "../store/formStore";
import { useUiStore } from "../store/uiStore";
import {
  getSubmitInFlight,
  clearSubmitInFlight,
  getSubmitDeadlineMs,
  POLL_INTERVAL_MS,
} from "../services/submitInFlight";

/**
 * Recovers UI state when a form load lands mid-submit. If localStorage shows that a
 * submit was in flight when the tab last left (the user refreshed, lost connection,
 * etc.), poll SF until it resolves and either flip the UI to submitted (which renders
 * the Thank-You page via review mode) or, on timeout, warn the user and unblock the
 * form. Without this, a refresh during the 5-15s submit window can drop the user back
 * onto what looks like an editable draft — see [[submitInFlight]] for the full scenario.
 *
 * Intentionally runs once per form-load (deps: formId, loading). The poll's inner
 * cancelled flag protects against stale callbacks after unmount or formId change.
 */
export function useSubmitInFlightRecovery(formId: string | null, loading: boolean): void {
  useEffect(() => {
    if (loading || !formId) return;
    const startedAt = getSubmitInFlight(formId);
    if (startedAt === null) return;

    // If the form is ALREADY submitted by the time we look, recovery is moot —
    // reviewMode is already on. Just clean up the stale signal.
    if (useFormStore.getState().formStatus === "submitted") {
      clearSubmitInFlight(formId);
      return;
    }

    useUiStore.getState().setSubmitting(true);
    let cancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const deadline = getSubmitDeadlineMs(startedAt);

    const poll = async () => {
      const api = getApi();
      while (!cancelled && Date.now() < deadline) {
        await new Promise<void>((resolve) => {
          timeoutId = setTimeout(resolve, POLL_INTERVAL_MS);
        });
        if (cancelled) return;
        try {
          const fresh = await api.getIntakeForm(formId);
          if (cancelled) return;
          if (fresh.status === "submitted") {
            clearSubmitInFlight(formId);
            useFormStore.getState().setFormStatus("submitted");
            useUiStore.getState().setSubmitting(false);
            return;
          }
        } catch {
          // Transient lookup failure — keep polling until the deadline.
        }
      }
      if (!cancelled) {
        // Deadline passed without SF flipping status — either the backend really
        // failed or it's taking pathologically long. Unblock the user with a
        // refresh-and-retry hint.
        clearSubmitInFlight(formId);
        useUiStore.getState().setSubmitting(false);
        useUiStore.getState().addToast(
          "We couldn't confirm your submission. Refresh the page; if it's still not submitted, try again.",
          "warning",
        );
      }
    };

    void poll();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [formId, loading]);
}
